import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
// services
import { MasterService } from "../../../services/master.service";
import { MessageService } from "../../../services/message.service";
import { StoreService } from "../../../services/store.service";
import { SecurityService } from "src/app/services/security.service";
import * as languageLibrary from '../../../services/language';
import { SCROLL_BOTTOM_OUT, SEARCH_DEBOUNCE_MILLIS } from "src/app/constants/list";
import { AutomationEntry } from "src/app/models/automation-entry.model";
import { Subscription } from "rxjs";
import { Lot } from "src/app/models/lot.model";

@Component({
    selector: "automation-report",
    templateUrl: "./automation-report.component.html",
    styleUrls: ["./automation-report.component.scss"],
    standalone: false
})
export class AutomationReportComponent implements OnInit, OnDestroy {

    protected FILTER_SPAN_KEY = 'flt-autolog-when'
    protected FILTER_LOT_KEY = 'flt-autolog-lot'
    protected FILTER_TYPE_KEY = 'flt-autolog-type'

    /** define if is loading */ 
    protected loading: boolean = false

    /** Indicates the contents of a tab were recently copied to clipboard, For UI confirmation. */
    protected copied: boolean = false

    // save the language
    protected language: string | null = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    /** A list of lots available to the user. */
    protected lots: Lot[] = [];

    // A list od user automation log entries for the report.
    protected entries: AutomationEntry[] = []

    // The currently selected lot
    protected lotId: number = 0

     // Current page the user is viewing (last loaded).
    protected page = 1

    // The number contacts currently being displayed.
    protected displayed = 0

    // Indicates whether or not there are no more records to load.
    protected exhausted = false

    // Total number of records available
    protected records: number = 0

    // The search form group
    protected form: FormGroup | null = null

    private subscriptions: Subscription[] = []

    // Keep track of how the user wants to sort list.
    private sortables: Object = {'startedAt': 'DESC'}

    // The automation entry whose meta data is currently being viewed.
    protected viewable: AutomationEntry | null = null

    constructor(private master: MasterService, private ms: MessageService, protected store: StoreService, private router: Router, private fb: FormBuilder, protected security: SecurityService) {
        this.form = this.fb.group({
            span: new FormControl<string>('', []),
            from: new FormControl<string>('', []),
            to: new FormControl<string>('', []),
            lot: new FormControl<number>(0, []),
            type: new FormControl<string>('', [])
        })
    }

    ngOnInit() {
        this.lotId = parseInt(localStorage.getItem('lot') || this.store.lotSelected)

        this.subscriptions.push(
            this.form.get('span').valueChanges.subscribe(span => {
                this.writeFilters()
                this.reset()            
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('type').valueChanges.subscribe(jobType => {
                this.writeFilters()
                this.reset()            
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('lot').valueChanges.subscribe(lot => {
                this.writeFilters()
                this.reset()            
                this.fetch()
            })
        )

        this.readFilters()
        this.determineLots()
        this.fetch()
    }

    ngOnDestroy(): void {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        this.subscriptions = []
    }

    /**
     * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.page = 1
        this.exhausted = false
        this.displayed = 0

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    /**
     * Handle scroll events of the list container and load 
     * the next page when avilable and the user scrolled 
     * to the bottom.
     * @param e The scroll event that trigggered this handler.
     */
    protected scrolled = (e): void => {
        if (!e || !e.target) {
            return
        }

        if (this.exhausted) {
            return
        }

        if (this.loading) {
            return
        }

        const location = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop)

        if (location >= SCROLL_BOTTOM_OUT) {
            return
        }

        this.page = this.page + 1
        this.fetch()
    }

    /**
     * 
     * @param field The field that user wants to sort by. 
     */
    protected sort = (field: string): void => {
        if (!field || (field.length <= 0)) {
            return
        }

        if (this.sortables[field]) { // need to toggle the direction?
            switch (this.sortables[field]) {
                case 'ASC':
                    this.sortables[field] = 'DESC'
                    break
                default:
                    this.sortables[field] = 'ASC'
                    break
            }

            this.reset()
            this.fetch()
            return
        }

        this.sortables = {}
        this.sortables[field] = 'ASC'
        this.reset()
        this.fetch()
    }

    protected sortedAsc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'ASC')
    }

    protected sortedDesc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'DESC')
    }

    /**
     * Query for a list of sales.
     * @param lot Unique identification of the selected lot
     */
    protected fetch = async () => {
        if (this.security.notPermitted('reports', 'automation log', 'view')) {
            return
        }

        if (this.loading) { // already getting next page
            return
        }
  
        if (this.exhausted) { // currently showing all available records?
            return
        }
  
        this.loading = true

        let spanParam = ''
        const spanKey = this.form.get('span').value
        if (spanKey && (spanKey.length > 0)) {
            switch (spanKey) {
                case 'custom':
                    const from = this.form.get('from').value
                    const to = this.form.get('to').value
                    spanParam = `&from=${from}&to=${to}`
                    break
                default:
                    spanParam = `&span=${spanKey}`
                    break
            }
        }

        let lotParam = ''
        const lot = parseInt(this.form.get('lot').value)
        if (lot && (lot > 0)) {
            lotParam = `&lotId=${lot}`
        }

        let typeParam = ''
        const type = this.form.get('type').value
        if (type && (type.trim().length > 0)) {
            typeParam = `&jobType=${encodeURIComponent(type)}`
        }
  
        let sortParam = ''
        if (this.sortables && (Object.keys(this.sortables).length > 0)) {
            const sorts = []
            for (const key of Object.keys(this.sortables)) {
                let direction = ''
                if (this.sortables[key] && this.sortables[key].length > 0) {
                    direction = `|${this.sortables[key]}`
                }
  
                sorts.push(`${key}${direction}`)
            }
  
            sortParam = `&sort=${sorts.join(',')}`
        }

        this.loading = true
        await this.master.getAsync(`automations?page=${this.page}${sortParam}${spanParam}${lotParam}${typeParam}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            this.records = res.data.records
            this.displayed = Math.min(this.records, (res.data.page * res.data.ipp))
            this.exhausted = (res.data.next === null)

            if (this.page > 1) {
                this.entries = this.entries.concat(res.data.entries)
                return
            }

            this.entries = res.data.entries
        })
    }

    /**
     * Check session storage for stored filters and apply them if found.
     */
    private readFilters = () => {
        if (!sessionStorage) {
            return
        }

        const spanFilter = sessionStorage.getItem(this.FILTER_SPAN_KEY)
        if (spanFilter && (spanFilter.length > 0)) {
            if (spanFilter.startsWith('custom')) {
                const items = spanFilter.split(';')
                if (!items || (items.length !== 3)) {
                    return
                }

                this.form.get('span').setValue(items[0], {emitEvent: false})
                this.form.get('from').setValue(items[1], {emitEvent: false})
                this.form.get('to').setValue(items[2], {emitEvent: false})    

                return
            }

            this.form.get('span').setValue(spanFilter, {emitEvent: false})
        }
        else {
            this.form.get('span').setValue('today', {emitEvent: false})
            sessionStorage.setItem(this.FILTER_SPAN_KEY, `today`)
        }

        const lotFilter = sessionStorage.getItem(this.FILTER_LOT_KEY)
        if (lotFilter && (lotFilter.length > 0)) {
            this.form.get('lot').setValue(lotFilter.toString(), {emitEvent: false})
        }

        const typeFilter = sessionStorage.getItem(this.FILTER_TYPE_KEY)
        if (typeFilter && (typeFilter.length > 0)) {
            this.form.get('type').setValue(typeFilter.toString(), {emitEvent: false})
        }
    }

    /**
     * Save the existing list fitlers to session storage for later retrieval.
     */
    private writeFilters = () => {
        if (!sessionStorage) {
            return
        }

        const spanFilter = this.form.get('span').value
        if (!spanFilter || (spanFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_SPAN_KEY)
        }
        else {
            let fromTo = ''
            if (spanFilter === 'custom') {
                const from = this.form.get('from').value
                const to = this.form.get('to').value
                fromTo = `;${from};${to}`
            }

            sessionStorage.setItem(this.FILTER_SPAN_KEY, `${spanFilter}${fromTo}`)
        }

        const lotFilter = this.form.get('lot').value
        if (!lotFilter || (lotFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_LOT_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_LOT_KEY, lotFilter)
        }

        const typeFilter = this.form.get('type').value
        if (!typeFilter || (typeFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_TYPE_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_TYPE_KEY, typeFilter)
        }
    }

    /**
     * Handle date span choices by the user.
     * @param choice 
     */
    protected spanned = (choice: Object) => {
        if (!choice || (choice['key'] === undefined) || (choice['key'] === null)) {
            return
        }

        if (choice['key'] === 'custom') {
            const from = choice['from']
            const to = choice['to']
            if (!from || !to) {
                return
            }

            this.form.get('from').setValue(from, {emitEvent: false})
            this.form.get('to').setValue(to, {emitEvent: false})    
        }

        this.form.get('span').setValue(choice['key'])
    }

    /**
     * 
     * @param entry 
     */
    protected view = (entry: AutomationEntry) => {
        if (!entry || !entry.meta) {
            this.viewable = null
            return
        }

        const btn = document.getElementById('btn-toggle-automation-meta-view') as HTMLButtonElement
        if (!btn) {
            this.viewable = null
            return
        }

        this.viewable = entry
        btn.click()
    }

    /**
     * Return automation log meta when available.
     */
     protected meta = () => {
        if (!this.viewable || !this.viewable.meta) {
            return ''
        }

        return JSON.stringify(this.viewable.meta, null, '\t')
    }

    /**
     * 
     * @param status 
     * @returns 
     */
    protected badgeClass = (status: string): string => {
        if (!status) {
            return 'badge badge-pending'
        }

        switch (status.toLocaleLowerCase()) {
            case 'running':
                return 'badge badge-'
            case 'success':
                return 'badge badge-success'
            case 'failure':
                return 'badge badge-danger'
            default:
                return 'badge badge-secondary'
        }
    }
    
    /**
     * listen to output of navbar
    */ 
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'setPermissions':
                this.determineLots()
                this.reset()
                this.fetch()
                break
        }
    }

    private determineLots = () => {
        const user = this.store.userAccount['user']
        if (!user || !user.lots) {
            return
        }

        this.lots = user.lots
    }

    /**
     * Copy the log entry meta contents to the clipboard
     * @param e Event that trigger the copy to clipboard
     */
    protected clip = (e: Event): void => {
        if (!navigator || !navigator.clipboard) {
            return
        }

        this.copied = true
        setTimeout(() => {
            this.copied = false
        }, 3000)

        navigator.clipboard.writeText(this.meta())
    }
}
